import { Plural, T, Var } from 'gt-react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { WarningIcon } from '@phosphor-icons/react';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { ArTaskActionButton } from '@/components/accounts-receivable/tasks/ArTaskActionButton';
import { useArCollectionThreadReminderTask } from '@/api/rpc/hooks/accounts-receivable/useArCollectionThreads';
import { cn } from '@/lib/utils';
import {
  deriveThreadReminderReason,
  type ThreadReminderReason,
} from './threadReminderReason';
import type { CollectionThreadReminderTaskDto } from '@erp/rpc';

interface ThreadReminderBannerProps {
  threadId: string;
  /** Layout classes from the host (margins, flex sizing). */
  className?: string;
}

/**
 * Pinned banner explaining why the system pulled the user back to this thread.
 *
 * Renders only when an AR task is outstanding against the thread (see
 * `arCollectionThreads.getReminderTask`) — no reminder means no banner, not an
 * empty state. It answers four questions in order: what to do next, *why* we
 * flagged it, when the reminder went out, and who owns it. The action
 * affordance is the shared `ArTaskActionButton` so completing from here behaves
 * exactly like completing from the inbox or the AR dashboard.
 */
export function ThreadReminderBanner({
  threadId,
  className,
}: ThreadReminderBannerProps) {
  const { reminderTask } = useArCollectionThreadReminderTask(threadId);
  if (!reminderTask) return null;
  return <ThreadReminderBannerBody task={reminderTask} className={className} />;
}

/**
 * Split out so tests (and any future caller that already holds the task) can
 * render the banner without the query layer.
 */
export function ThreadReminderBannerBody({
  task,
  className,
}: {
  task: CollectionThreadReminderTaskDto;
  className?: string;
}) {
  const reason = deriveThreadReminderReason(task.metadata);
  const issued = toDate(task.createdAt);

  return (
    <AlertContainer
      className={cn('border-amber-300 bg-amber-50 shadow-sm', className)}
      data-testid="thread-reminder-banner"
    >
      <WarningIcon className="h-4 w-4 shrink-0 text-amber-600" weight="fill" />
      <div className="ml-2 flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <AlertDescription className="min-w-0 text-amber-900">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
            <T context="heading on the banner telling a collector why the system flagged this collection thread">
              Gently needs you here
            </T>
          </div>
          <div className="mt-0.5 text-sm font-semibold text-amber-900">
            <NextStepText reason={reason} fallbackTitle={task.title} />
          </div>
          <div className="mt-1 text-sm text-amber-800">
            <ReasonText
              reason={reason}
              fallbackDescription={task.description}
            />
          </div>
          <div className="mt-1.5 text-xs text-amber-700">
            {issued ? (
              <span title={format(issued, "MMM d, yyyy 'at' h:mm a")}>
                <T context="when the system filed the reminder for this collection thread">
                  Flagged{' '}
                  <Var>{formatDistanceToNow(issued, { addSuffix: true })}</Var>
                </T>
              </span>
            ) : null}
            {issued ? <span className="mx-1.5">·</span> : null}
            {task.assigneeName ? (
              <T context="who the collection thread reminder is assigned to">
                Assigned to <Var>{task.assigneeName}</Var>
              </T>
            ) : (
              <T context="the collection thread reminder has no assignee">
                Unassigned — anyone on the team can pick this up
              </T>
            )}
            {task.displayId ? (
              <>
                <span className="mx-1.5">·</span>
                <span className="font-mono">{task.displayId}</span>
              </>
            ) : null}
          </div>
        </AlertDescription>
        <div className="flex shrink-0 items-center gap-1">
          {/* The deep link would point at the page we're already on. */}
          <ArTaskActionButton task={task} hideThreadLink />
        </div>
      </div>
    </AlertContainer>
  );
}

/** What the system wants done — the headline directive. */
function NextStepText({
  reason,
  fallbackTitle,
}: {
  reason: ThreadReminderReason;
  fallbackTitle: string;
}) {
  switch (reason.kind) {
    case 'reply_review':
      return reason.hasDraft ? (
        <T context="next step on a collection thread when a suggested reply is waiting for review">
          Review the suggested reply and send it.
        </T>
      ) : (
        <T context="next step on a collection thread when the customer replied but no draft was generated">
          Read the customer's reply and respond.
        </T>
      );
    case 'no_response':
      return (
        <T context="next step on a collection thread whose outbound email went unanswered">
          Follow up with the customer — nudge them again or escalate.
        </T>
      );
    default:
      // Unrecognized payload: the task's own title is the best directive we
      // have, and it's already written as an instruction by the scan.
      return <>{fallbackTitle}</>;
  }
}

/** Why the system decided this needed a human — the triggering signal. */
function ReasonText({
  reason,
  fallbackDescription,
}: {
  reason: ThreadReminderReason;
  fallbackDescription: string | null;
}) {
  switch (reason.kind) {
    case 'reply_review':
      return reason.hasDraft ? (
        <T context="reason a collection thread was flagged: customer replied and a draft is ready">
          The customer replied on this thread, so Gently drafted a response for
          you to check before it goes out.
        </T>
      ) : (
        <T context="reason a collection thread was flagged: customer replied but drafting failed">
          The customer replied on this thread and Gently couldn't draft a
          response, so it needs your words.
        </T>
      );
    case 'no_response': {
      const sent = reason.sentAt ? toDate(reason.sentAt) : null;
      const days = (
        <>
          <Var>{reason.daysWaiting}</Var>{' '}
          <Plural n={reason.daysWaiting} one="day" other="days" />
        </>
      );
      return (
        <>
          {reason.counterpartyEmail ? (
            <T context="reason a collection thread was flagged: an email to a specific address went unanswered">
              We emailed <Var>{reason.counterpartyEmail}</Var> and there's still
              been no reply after <Var>{days}</Var>.
            </T>
          ) : (
            <T context="reason a collection thread was flagged: an email went unanswered, recipient unknown">
              Our last email on this thread has gone <Var>{days}</Var> without a
              reply.
            </T>
          )}
          {sent ? (
            <>
              {' '}
              <T context="when the unanswered collection email was sent">
                Sent <Var>{format(sent, 'MMM d, yyyy')}</Var>.
              </T>
            </>
          ) : null}
        </>
      );
    }
    default:
      // No recognized payload — fall back to whatever the scan wrote.
      return fallbackDescription ? <>{fallbackDescription}</> : null;
  }
}

/** Parse an ISO string, returning null rather than an Invalid Date. */
function toDate(iso: string): Date | null {
  const parsed = parseISO(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
